import React, {
  useEffect,
  useState,
  FunctionComponent,
  useId,
  useRef
} from 'react';
import { rovingIndex } from 'roving-ux';
import { StandaloneSearchBox as HeadlessSearchBox } from '@coveo/headless';
import { SEARCH_KEYS } from '../controllers/search-keys';
import { secureStorage } from '../../helpers/global-storage';
import { isTablet } from '../../helpers/breakpoints';

interface SearchBoxProps {
  controller: HeadlessSearchBox;
  searchLabel: string;
  searchPlaceholder: string;
  submitLabel: string;
  clearLabel: string;
  viewResults: string;
  maxSuggestions?: number;
}

export const SEARCH_BOX_EVENTS = Object.freeze({
  updateDidYouMean: 'searchbox:updateDidYouMean'
});

export const StandaloneSearchBox: FunctionComponent<SearchBoxProps> = props => {
  const id = useId();
  const {
    controller,
    searchLabel,
    searchPlaceholder,
    clearLabel,
    viewResults,
    maxSuggestions
  } = props;
  const [state, setState] = useState(controller.state);
  const [value, setValue] = useState(controller.state.value);
  const [focus, setFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const input = useRef<HTMLInputElement>(null);

  const isEnterKey = (e: React.KeyboardEvent<HTMLDivElement>) =>
    e.key === 'Enter';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (input.current === document.activeElement) {
      if (isEnterKey(e)) {
        e.preventDefault();
        controller.updateText(value);
        controller.submit();
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (listRef.current) {
          if (e.key === 'ArrowDown') {
            const firstElement = listRef.current.querySelector('li');
            if (firstElement) {
              firstElement.querySelector('button')?.focus();
              setFocus(true);
              setOpen(true);
            }
          } else {
            const lastElement = listRef.current.querySelector('li:last-child');
            if (lastElement) {
              (lastElement as HTMLLIElement).querySelector('button')?.focus();
              setFocus(true);
              setOpen(true);
            }
          }
        }
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        input.current?.blur();
      }
    } else {
      if (e.key === 'Escape') {
        e.preventDefault();
        input.current?.focus();
        setFocus(false);
      }
      if (
        (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
        e.currentTarget.querySelector('.field--search')
      ) {
        e.preventDefault();
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 2) {
      controller.updateText(e.target.value);
      setFocus(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (e.target.value.length > 2) {
      controller.updateText(e.target.value);
    }
  };

  const handleSuggestionClick = (value: string) => {
    setValue(value);
    controller.selectSuggestion(value);
  };

  const handleMouseDown = (e: Event) => {
    if ((e.target as HTMLElement)?.closest('.header__search')) {
      if ((e.target as HTMLElement).className === 'header__search-trigger') {
        return;
      } else {
        setOpen(true);
      }
    } else {
      if (open) {
        setOpen(false);
        document.querySelector('.header')?.classList.remove('mobile-search');
      }
    }
  };

  const handleDidYouMean = (e: CustomEventInit) => {
    const query = e.detail.query;
    setValue(query);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setValue('');
    controller.updateText('');
  };

  const handleSearchTrigger = () => {
    setOpen(prev => !prev);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLElement>) => {
    if (e.relatedTarget) {
      if (!(e.relatedTarget as HTMLElement)?.closest('.header__search')) {
        setOpen(false);
        setFocus(false);
      }
    } else {
      return;
    }
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLElement>) => {
    const _input = e.currentTarget;
    setFocus(true);

    const parentHeader = _input.closest('.header');
    if (parentHeader?.classList.contains('open')) {
      parentHeader.classList.add('mobile-search');
    }
    if (isTablet) {
      setTimeout(() => {
        _input.scrollIntoView({ behavior: 'smooth' });
      }, 320);
    }
  };

  const handleIconSearchClick = () => {
    controller.updateText(value);
    controller.submit();
  };

  useEffect(() => {
    controller.subscribe(() => setState(controller.state));
    window.addEventListener(
      SEARCH_BOX_EVENTS.updateDidYouMean,
      handleDidYouMean
    );

    return () => {
      window.removeEventListener(
        SEARCH_BOX_EVENTS.updateDidYouMean,
        handleDidYouMean
      );
    };
  }, []);

  useEffect(() => {
    if (listRef.current) {
      rovingIndex({
        element: listRef.current,
        target: '.predictive-search__item'
      });
    }
  }, [state.suggestions]);

  useEffect(() => {
    if (input.current && open) {
      input.current.focus();
    }

    window.addEventListener('mousedown', e => handleMouseDown(e));
    return () => {
      window.removeEventListener('mousedown', e => handleMouseDown(e));
    };
  }, [open, input.current]);

  if (state.redirectTo) {
    const { redirectTo, value, analytics } = state;
    const data = JSON.stringify({ value, analytics });
    secureStorage.set(SEARCH_KEYS.globalSearch, data);
    window.location.href = redirectTo;
  }

  if (!open) {
    return (
      <button
        className="header__search-trigger"
        title={searchLabel}
        aria-label={searchLabel}
        onClick={handleSearchTrigger}
        data-layer='[{"event": "navigation", "event_category": "navigation", "event_action": "top nav", "event_label":"search" }]'
      >
        {searchLabel}
      </button>
    );
  }

  return (
    <div onKeyDown={e => handleKeyDown(e)}>
      <div className="field field--search">
        <input
          className="field__input header__search-input "
          type="text"
          id={`searchbox-${id}`}
          name="search"
          placeholder={searchPlaceholder}
          value={value}
          onChange={handleChange}
          onInput={handleInputChange}
          ref={input}
          onFocus={handleInputFocus}
          onBlur={e => handleInputBlur(e)}
        />
        {state.suggestions.length > 0 && value.length > 2 && focus && (
          <ul
            className="predictive-search__list header__predictive-search"
            ref={listRef}
            onFocus={() => setFocus(true)}
          >
            {state.suggestions.map((suggestion, index) => {
              const value = suggestion.rawValue;
              if (maxSuggestions && index >= maxSuggestions - 1) {
                return null;
              }
              return (
                <li key={value}>
                  <button
                    className="predictive-search__item"
                    title={suggestion.rawValue}
                    onClick={() => handleSuggestionClick(value)}
                    dangerouslySetInnerHTML={{
                      __html: suggestion.highlightedValue
                    }}
                    onFocus={() => {
                      setFocus(true);
                    }}
                  />
                </li>
              );
            })}
            <li>
              <button
                className="predictive-search__item link link--arrow link--small"
                title={viewResults}
                onClick={() => {
                  controller.updateText(value);
                  controller.submit();
                }}
                onFocus={() => {
                  setFocus(true);
                }}
              >
                <span>{viewResults}</span>
              </button>
            </li>
          </ul>
        )}
        {value.length > 0 && (
          <button
            className="field__input-close header__input-close"
            title={clearLabel}
            aria-label={clearLabel}
            onClick={handleClear}
          ></button>
        )}
        <button
          className="field__input-search header__input-search"
          title={searchLabel}
          aria-label={searchLabel}
          onClick={handleIconSearchClick}
          onBlur={e => handleInputBlur(e)}
          disabled={state.value.length === 0}
        ></button>
      </div>
    </div>
  );
};
